/* eslint-disable react/jsx-no-constructed-context-values */
import React, { createContext, useEffect, useContext, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { uniqBy, cloneDeep, set, isEmpty, get } from 'lodash';
import { useTranslation } from 'react-i18next';
import { getBomDetailsBasedOnId } from '../../store/DevelopmentOrder.action';
import noImage from '../../../../assets/images/no-image.png';
import * as action from '../store/BOMConfig.action';
import * as libraryAction from '../../../Library/BomLibrary/store/BomLibrary.action';
import * as supportingAction from '../../../../Components/SupportingDocument/store/SupportingDocument.action';
import * as supplierAction from '../../../Supplier/store/Supplier.action';
import { pageSize } from '../../../../Components/helper';
import { mergeSupportingDocuments } from '../../../../Components/SupportingDocument/SupportingDocumentTable';

export const StateContext = createContext(null);

export default function PIMStateProvider({ children }) {
  const initialSetting = {
    cardList: [],
    allSelected: false,
    tableView: false,
    loading: false,
    pageLoading: false,
    pageSize: 20,
    pageIndex: 1,
    hasMore: false,
    selectedIds: [],
    openPreview: false,
    previewUrl: '',
    showTestReq: false,
    testReqData: null,
    showBomUsage: false,
    bomUsageData: null,
    showAddtoLibrary: false,
    isMultiEditing: false,
    addToLibraryData: null,
    editBomDataId: [],
    referenceSupp: [],
    expandedIds: [],
    allExpanded: false,
    bomTypeId: '',
    imageUpload: false,
    uploadImageData: null,
    sendDocument: false,
    imageName: '',
    imageType: '',
    imageSize: '',
    originalImage: '',
    imgState: { mode: false, data: '' },
    showImageRemove: false,
    removeId: -1,
    colorDocumentId: [],
    openPopOverId: '',
    documents: [],
  };
  const dispatch = useDispatch();
  const { devOrderId } = useParams();
  const { t } = useTranslation('common');
  const {
    UserDetails: { userInfo },
    SupportingDocument: { mergedUrl, supportingDocs },
    SuppliersDetails: { tier2AndAboveList, tier1Suppliers },
    BomLibraryDetails: { colourDocs },
    BOMConfigDetails: { cardList },
    Measuremets: { bomDetailsBasedOnId },
    ColorLibraryDetails: { ColorLibList },
  } = useSelector((redState) => redState);

  const loggedInUserId = userInfo.userId;
  const userRole = userInfo.role;

  const [bomState, setBomState] = useState(initialSetting);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (bomState?.editBomDataId?.length > 0 && bomState?.bomTypeId) {
          await dispatch(getBomDetailsBasedOnId(devOrderId, bomState.editBomDataId, bomState.bomTypeId));
        }
      } catch (error) {
        console.error('Failed to fetch BOM details:', error);
      }
    };

    fetchData();
  }, [dispatch, devOrderId, bomState?.editBomDataId, bomState?.bomTypeId]);

  useEffect(() => {
    const initialApiCalls = async () => {
      dispatch(
        libraryAction.fetchBomLibarayUnitOfMeasure({
          isActive: true,
          loggedInUserId,
        }),
      );
    };
    if (bomState?.bomTypeId) initialApiCalls();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bomState?.bomTypeId, dispatch]);

  useEffect(() => {
    let rs = [];
    if (tier2AndAboveList || tier1Suppliers) {
      const rsArray = uniqBy([...tier1Suppliers, ...tier2AndAboveList], 'name');
      const uniqueRs = [];
      const uniqueRsArray = [];
      rsArray.forEach((val) => {
        if (uniqueRs.indexOf(val?._id) === -1) {
          uniqueRs.push(val?._id);
          uniqueRsArray.push(val);
        }
      });
      rs.push(...uniqueRsArray);
      rs.forEach((ele) => {
        ele.label = ele?.name;
        ele.value = ele?._id;
      });
      const checkuserRole = async () => {
        if (userRole === 'User') {
          const userSuppliers = dispatch(supplierAction.fetchUserSuppliers({ userId: loggedInUserId }));
          if (Array.isArray(userSuppliers)) {
            rs = rs.filter((val) => {
              if (userSuppliers.includes(val._id)) return val;
            });
          }
        }
      };
      checkuserRole();

      setBomState((prevState) => ({
        ...prevState,
        referenceSupp: rs,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tier2AndAboveList, tier1Suppliers, dispatch]);

  useEffect(() => {
    if (mergedUrl) {
      setBomState((prevState) => ({
        ...prevState,
        previewUrl: mergedUrl,
      }));
    }
  }, [mergedUrl]);

  useEffect(() => {
    if (supportingDocs?.length > 0 || colourDocs?.length > 0) {
      setBomState((prevState) => ({ ...prevState, documents: mergeSupportingDocuments(colourDocs, supportingDocs) }));
    }
  }, [supportingDocs, colourDocs]);

  useEffect(() => {
    if (!isEmpty(colourDocs)) {
      const c = bomState?.colorDocumentId?.find((o) => o._id === colourDocs[0]._id);
      if (!c) {
        setBomState((oldState) => ({ ...oldState, colorDocumentId: [...oldState.colorDocumentId, colourDocs[0]] }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colourDocs]);

  const importColourWayDoc = async (e) => {
    dispatch(
      libraryAction.fetchColourWayDocs({
        colourId: e?.colourWayId ? e?.colourWayId : e?._id,
      }),
    );
  };

  const handleSelectAll = () => {
    const selectedIds = bomState?.cardList?.map((card) => card?._id);
    setBomState((prevState) => ({
      ...prevState,
      selectedIds: bomState?.selectedIds?.length > 0 ? [] : [...selectedIds],
      allSelected: !bomState?.allSelected,
    }));
  };

  const onSelectCheckBox = (value, id) => {
    const selectedIds = value ? [...(bomState.selectedIds ?? []), id] : bomState?.selectedIds?.filter((e) => e !== id);
    setBomState((prevState) => ({
      ...prevState,
      selectedIds: [...selectedIds],
      allSelected: bomState?.cardList?.length === selectedIds?.length,
    }));
  };

  const handleMergedPreview = async (ele) => {
    const params = {
      bomType: ele?.bomType?._id,
      devOrderId,
      bomId: ele?._id,
      libId: ele?.libId,
    };
    const res = dispatch(supportingAction.getMergedDocs(params, t));
    if (res)
      setBomState((prevState) => ({
        ...prevState,
        openPreview: true,
      }));
  };
  const toggleFP = async () => {
    setBomState((prevState) => ({
      ...prevState,
      openPreview: false,
    }));
    const mergedDocs = mergedUrl.split('mergedDocuments/');
    const id = mergedDocs[1].split('.');
    const params = {
      id: id[0],
    };
    dispatch(supportingAction.deleteMergedDocs(params));
  };

  const OpenTestReq = async (ele) => {
    setBomState((prevState) => ({
      ...prevState,
      testReqData: ele,
      showTestReq: true,
    }));
  };

  const toggleTestReq = async () => {
    setBomState((prevState) => ({
      ...prevState,
      testReqData: null,
      showTestReq: false,
    }));
  };

  const openBomUsage = async (ele) => {
    setBomState((prevState) => ({
      ...prevState,
      bomUsageData: ele,
      showBomUsage: true,
    }));
  };

  const toggleBomUsage = async () => {
    setBomState((prevState) => ({
      ...prevState,
      bomUsageData: null,
      showBomUsage: false,
    }));
  };

  const handleImportLib = async (ele) => {
    setBomState((prevState) => ({
      ...prevState,
      addToLibraryData: ele,
      showAddtoLibrary: true,
    }));
  };

  const toggleAddToLibrary = async () => {
    setBomState((prevState) => ({
      ...prevState,
      addToLibraryData: null,
      showAddtoLibrary: false,
    }));
  };

  const handleEdit = async (ele) => {
    if (ele === null && bomState?.selectedIds?.length > 0) {
      setBomState((prevState) => ({
        ...prevState,
        editBomDataId: bomState?.selectedIds,
        isMultiEditing: true,
      }));
    } else {
      setBomState((prevState) => ({
        ...prevState,
        editBomDataId: [ele?._id],
        // colorDocumentId: ele?.docIds?.map((e) => e?._id),
      }));
    }
  };

  const handleCancel = async (id) => {
    if (bomState?.isMultiEditing) {
      let bomCards = cloneDeep(bomState?.cardList);
      const bomIndex = bomCards?.findIndex((ele) => ele?._id === id);
      if (bomIndex !== -1) {
        const tempReducerData = cloneDeep(cardList);
        const tableCards = tempReducerData.map((item) => {
          let combisupplier = [];
          if (item?.combinationSuppliers) {
            combisupplier = item?.combinationSuppliers?.map((ele) => {
              return ele.componentSupplier;
            });
          }

          if (!item?.composition) {
            item.composition = [{ percentage: null, composition: null }];
          }
          return {
            ...item,
            colourComponentSuppliers: [...(item?.BOMSupplier ?? []), ...combisupplier]
              ?.flatMap((e) => e)
              .map((mapEle) => ({ label: mapEle?.name, value: mapEle?._id })),
            colourWayRefsData: item?.colourWayRefsData,
            isExpanded: true,
            hasCombination: item?.combinationSuppliers?.length > 0,
          };
        });
        const oldObj = tableCards[bomIndex];
        const obj = bomCards[bomIndex];
        if (obj?.error) {
          obj.error = false;
        }
        bomCards[bomIndex] = { ...oldObj };
      } else {
        bomCards = bomCards?.filter((e) => e?._id);
      }
      setBomState((prevState) => ({
        ...prevState,
        editBomDataId: bomState?.editBomDataId?.filter((ele) => ele !== id),
        selectedIds: bomState?.selectedIds?.filter((ele) => ele !== id),
        isMultiEditing: !isEmpty(bomState?.selectedIds?.filter((ele) => ele !== id)),
        cardList: bomCards,
      }));
    } else {
      let bomCards = cloneDeep(bomState?.cardList);
      const bomIndex = bomCards?.findIndex((ele) => bomState?.editBomDataId?.includes(ele?._id));
      if (bomIndex !== -1) {
        const obj = bomCards[bomIndex];
        if (obj?.error) {
          obj.error = false;
        }
        const tempReducerData = cloneDeep(cardList);
        const tableCards = tempReducerData.map((item) => {
          // initial colourway data object added and colourWayIndex and colourWayRefIndex need to be 0 to get the changes for the

          let combisupplier = [];
          if (item?.combinationSuppliers) {
            combisupplier = item?.combinationSuppliers?.map((ele) => {
              return ele.componentSupplier;
            });
          }

          if (!item?.composition) {
            item.composition = [{ percentage: null, composition: null }];
          }
          return {
            ...item,
            colourComponentSuppliers: [...(item?.BOMSupplier ?? []), ...combisupplier]
              ?.flatMap((e) => e)
              .map((mapEle) => ({ label: mapEle?.name, value: mapEle?._id })),
            colourWayRefsData: item?.colourWayRefsData,
            isExpanded: true,
            hasCombination: item?.combinationSuppliers?.length > 0,
          };
        });
        const oldObj = tableCards[bomIndex];
        bomCards[bomIndex] = { ...oldObj };
      } else {
        bomCards = bomCards?.filter((e) => e?._id);
      }
      setBomState((prevState) => ({
        ...prevState,
        editBomDataId: [],
        cardList: bomCards,
      }));
    }
  };

  const handleExpandAllView = () => {
    const selectedIds = bomState?.cardList?.map((card) => card?._id);
    setBomState((prevState) => ({
      ...prevState,
      expandedIds: bomState?.expandedIds?.length > 0 ? [] : [...selectedIds],
      allExpanded: !bomState?.allExpanded,
    }));
  };
  const expandLineItem = (cardId) => {
    const hasCardId = bomState?.expandedIds.includes(cardId);
    if (hasCardId) {
      const updatedIds = bomState?.expandedIds.filter((ele) => ele !== cardId);
      setBomState((prevState) => ({
        ...prevState,
        expandedIds: [...updatedIds],
        allExpanded: false,
      }));
    } else {
      const addedIds = [...(bomState.expandedIds ?? []), cardId];
      setBomState((prevState) => ({
        ...prevState,
        expandedIds: addedIds,
        allExpanded: addedIds?.length === bomState?.cardList?.length,
      }));
    }
  };

  const getColourRefOptionsForCard = (card, colorLibList) => {
    if (!Array.isArray(colorLibList) || !colorLibList.length) return [];
    const filtered = colorLibList.filter((el) => !el.isDeleted);
    const libraryColourWays = card?.libraryColourWays;
    const hasLibraryColourRefs =
      Array.isArray(libraryColourWays) &&
      libraryColourWays.length > 0 &&
      libraryColourWays.some(
        (item) =>
          Array.isArray(item?.colourRefsDataAsPerSuppliers) &&
          item.colourRefsDataAsPerSuppliers.some((subItem) => subItem?.colourRef || subItem?.colourRefName),
      );
    if (hasLibraryColourRefs) {
      const colourRefIds = new Set();
      libraryColourWays.forEach((item) => {
        if (Array.isArray(item?.colourRefsDataAsPerSuppliers)) {
          item.colourRefsDataAsPerSuppliers.forEach((subItem) => {
            if (subItem?.colourRef) {
              const id = typeof subItem.colourRef === 'object' ? subItem.colourRef._id : subItem.colourRef;
              if (id) colourRefIds.add(id);
            }
            if (subItem?.colourRefName) {
              const id = typeof subItem.colourRefName === 'object' ? subItem.colourRefName._id : subItem.colourRefName;
              if (id) colourRefIds.add(id);
            }
          });
        }
      });
      return filtered.filter((element) => colourRefIds.has(element._id));
    }
    return filtered;
  };

  const handleChange = (value, path, keyName, keyIndex) => {
    const clonedCardList = cloneDeep(bomState?.cardList);
    if (path?.includes('BOMSupplier')) {
      const newPath = path?.replace('BOMSupplier', 'colourComponentSuppliers');
      set(clonedCardList, newPath, [value]);
      set(clonedCardList, path, [value]);
    } else if (path?.includes('newPlacement')) {
      if (value?.length > 0) {
        clonedCardList[keyIndex]?.colourWayRefsData?.forEach((item) => {
          item?.colourRefsDataAsPerSuppliers?.forEach((subItem) => {
            const colourFoundInBOM = value?.find((item) => item?.value === subItem?.colourPlacement?._id);
            if (!colourFoundInBOM) {
              subItem.colourPlacement = null;
            }
          });
        });
      } else {
        clonedCardList[keyIndex]?.colourWayRefsData?.forEach((item) => {
          item?.colourRefsDataAsPerSuppliers?.forEach((subItem) => {
            subItem.colourPlacement = null;
          });
        });
      }
      set(clonedCardList, path, value);
    } else {
      if (path?.includes('colourWayId')) {
        importColourWayDoc(value);
        const card = clonedCardList[keyIndex];
        const validOptions = getColourRefOptionsForCard(card, ColorLibList || []);
        const isValidOption = value && validOptions?.some((opt) => opt?._id === value?._id);
        const colourIndexMatch = path.match(/colourWayRefsData\[(\d+)\]/);
        const colourIndex = colourIndexMatch ? parseInt(colourIndexMatch[1], 10) : 0;
        const colourRefData = card?.colourWayRefsData?.[colourIndex]?.colourRefsDataAsPerSuppliers;
        if (Array.isArray(colourRefData)) {
          const valueToSet = isValidOption ? value : null;
          colourRefData.forEach((_, colourRefIdx) => {
            set(clonedCardList, `[${keyIndex}].colourWayRefsData[${colourIndex}].colourRefsDataAsPerSuppliers[${colourRefIdx}][colourRef]`, valueToSet);
            set(clonedCardList, `[${keyIndex}].colourWayRefsData[${colourIndex}].colourRefsDataAsPerSuppliers[${colourRefIdx}][colourRefName]`, valueToSet);
          });
        }
      }
      if (path?.endsWith('[colourRef]')) {
        const refNamePath = path.replace(/\[colourRef\]$/, '[colourRefName]');
        set(clonedCardList, refNamePath, value);
      }
      set(clonedCardList, path, value);
    }
    setBomState((previousState) => ({ ...previousState, cardList: clonedCardList }));

    if (keyName && keyIndex !== -1 && clonedCardList[keyIndex]?._id) {
      const tempReducerData = cloneDeep(cardList);
      const tableCards = tempReducerData.map((item) => {
        // initial colourway data object added and colourWayIndex and colourWayRefIndex need to be 0 to get the changes for the
        const colourArray =
          item?.colourWayRefsData?.length > 0
            ? []
            : [
                {
                  approveBy: null,
                  autoApprove: null,
                  status: item?.colorAutoApprove || null,
                  comment: '',
                  commentDate: '',
                  colourWayIndex: 0,
                  colourWayRefIndex: 0,
                  colourWayImage: null,
                  colourWayId: null,
                  colourRef: null,
                  colourRefName: null,
                  tier1Supplier: null,
                  colourPlacement: null,
                  componentSupplier: null,
                },
              ];
        item?.colourWayRefsData?.forEach((element, i) => {
          element?.colourRefsDataAsPerSuppliers?.forEach((refrenceItem, refrenceIndex) => {
            colourArray.push({
              _id: refrenceItem?._id,
              autoApprove: refrenceItem?.autoApprove || false,
              status: refrenceItem?.status || null,
              approveBy: refrenceItem?.approveBy || null,
              commentDate: refrenceItem?.commentDate || null,
              comment: refrenceItem?.comment || '',
              colourWayIndex: i,
              colourWayRefIndex: refrenceIndex,
              colourWayImage: element?.colourWayId?.image || '',
              colourWayId: element?.colourWayId || null,
              colourRef: refrenceItem?.colourRef || null,
              colourRefName: refrenceItem?.colourRefName || null,
              tier1Supplier: refrenceItem?.tier1Supplier || null,
              componentSupplier: refrenceItem?.componentSupplier || null,
              colourPlacement: refrenceItem?.colourPlacement || null,
            });
          });
        });

        let combisupplier = [];
        if (item?.combinationSuppliers) {
          combisupplier = item?.combinationSuppliers?.map((item) => {
            return item.componentSupplier;
          });
        }

        if (!item?.composition) {
          item.composition = [{ percentage: null, composition: null }];
        }
        return {
          ...item,
          colourComponentSuppliers: [...(item?.BOMSupplier ?? []), ...combisupplier]
            ?.flatMap((e) => e)
            .map((mapEle) => ({ label: mapEle?.name, value: mapEle?._id })),
          isExpanded: true,
          hasCombination: item?.combinationSuppliers?.length > 0,
        };
      });
      const oldValue = get(tableCards, path);
      const newValue = get(clonedCardList, path);
      const newhighlightedValue =
        clonedCardList?.[keyIndex]?.highlightedKeys && !isEmpty(clonedCardList?.[keyIndex].highlightedKeys)
          ? [...(clonedCardList?.[keyIndex]?.highlightedKeys || [])]
          : [];
      const handleComposition = path.split('composition');
      if (oldValue !== newValue && !newhighlightedValue.includes(['composition', 'percentage'].includes(keyName) ? 'composition' : keyName)) {
        newhighlightedValue.push(['composition', 'percentage'].includes(keyName) ? 'composition' : keyName);
      }
      const highLightpath = ['composition', 'percentage'].includes(keyName)
        ? `${handleComposition[0]}highlightedKeys`
        : path?.replace(keyName, 'highlightedKeys');
      set(clonedCardList, highLightpath, [...newhighlightedValue]);
    }
  };

  const forCroppedImage = (obj) => {
    if (bomState?.imgState?.mode && bomState?.imgState?.data) {
      obj.image = bomState?.croppedImage;
      obj.fullImage = bomState?.originalImage;
    } else {
      obj.image = 'DoNotChange';
      obj.fullImage = 'DoNotChange';
    }
  };

  const forCroppedImageMultiple = (multiSave) => {
    const str = cardList?.filter((ele) => ele?._id === multiSave?._id);
    if (str[0]?.image === multiSave?.image) {
      multiSave = { ...multiSave, image: 'DoNotChange', fullImage: 'DoNotChange' };
    }
    return multiSave;
  };

  const colourDocIds = () => {
    return bomState?.colorDocumentId?.map((ele) => ele?._id);
  };

  const uniqDocIds = (supportingDocIds) => {
    const idsArray = cloneDeep([...supportingDocIds, ...colourDocIds()]);
    idsArray.forEach((ele, i) => {
      idsArray.forEach((element, index) => {
        if (i !== index && ele === element) {
          idsArray.splice(i, 1);
        }
      });
    });
    return idsArray;
  };

  const handleSameColourData = (array) => {
    // array is expected to be colourWayRefsData
    const uniqKeys = [];
    array?.forEach((colourWay) => {
      const colourWayId = colourWay?.colourWayId?._id ? colourWay?.colourWayId?._id : null;
      colourWay?.colourRefsDataAsPerSuppliers?.forEach((refData) => {
        const key =
          `${colourWayId ?? ''}` +
          `${refData?.colourRef?._id ?? refData?.colourRef?._id ?? ''}` +
          `${refData?.colourRefName?._id ?? refData?.colourRefName?._id ?? ''}` +
          `${refData?.colourPlacement?._id ?? refData?.colourPlacement?._id ?? ''}` +
          `${refData?.tier1Supplier?._id ?? refData?.tier1Supplier?._id ?? ''}` +
          `${refData?.componentSupplier?._id ? refData?.componentSupplier?._id : refData?.componentSupplier?.value}`;
        uniqKeys.push(key);
      });
    });
    return uniqKeys?.length !== new Set(uniqKeys).size;
  };

  const handleComposition = (compositionArray) => {
    const returnArray = [];
    compositionArray?.forEach((element) => {
      if (element?.percentage && element?.composition) {
        returnArray.push({ percentage: element?.percentage, composition: element?.composition?._id });
      }
    });
    return returnArray;
  };

  const handleColourway = (colourArray, dataColour) => {
    colourArray?.forEach((colourData) => {
      const colourWayId = colourData?.colourWayId?._id ?? null;
      // Find if this colourWayId already exists in dataColour
      const existing = dataColour.find((item) => item.colourWayId === colourWayId);
      const refData = [];
      colourData?.colourRefsDataAsPerSuppliers?.forEach((colourDataForRef) => {
        refData.push({
          colourRef: colourDataForRef.colourRef?._id ?? null,
          colourRefName: colourDataForRef.colourRefName?._id ?? null,
          componentSupplier: colourDataForRef?.componentSupplier?._id ?? colourDataForRef?.componentSupplier?.value ?? null,
          tier1Supplier: colourDataForRef?.tier1Supplier?._id ?? null,
          colourPlacement: (colourDataForRef?.colourPlacement?._id || colourDataForRef?.colourPlacement?.value) ?? null,
          _id: colourDataForRef?._id ?? null,
          autoApprove: colourDataForRef?.autoApprove ?? false,
          status: colourDataForRef?.status ?? null,
          approveBy: colourDataForRef?.approveBy ?? null,
          commentDate: colourDataForRef?.commentDate ?? null,
          comment: colourDataForRef?.comment ?? '',
        });
      });
      if (existing) {
        // Merge refs into the existing colourWayId entry
        existing.colourRefsDataAsPerSuppliers = [...(existing.colourRefsDataAsPerSuppliers || []), ...refData];
      } else {
        const object = {
          colourWayId,
          colourRefsDataAsPerSuppliers: refData,
        };
        dataColour.push(object);
      }
      // eslint-disable-next-line no-console
      // prettier-ignore
    });
  };

  const handleSave = async (obj) => {
    const bomCards = cloneDeep(bomState?.cardList);
    const bomIndex = bomCards?.findIndex((ele) => ele?._id === obj?._id);
    const colourArray = obj?.colourWayRefsData;
    const dataColour = [];
    const hasCombinationSuppliers = bomDetailsBasedOnId?.combinationSuppliers?.length > 0;
    let hasEmptyIndex = -1;
    if (hasCombinationSuppliers) {
      obj?.colourWayRefsData?.some((colourWay, colourWayIdx) => {
        return colourWay?.colourRefsDataAsPerSuppliers?.some((refData) => {
          if (!refData?.tier1Supplier?._id || !(refData?.componentSupplier?._id || refData?.componentSupplier?.value)) {
            hasEmptyIndex = colourWayIdx;
            return true;
          }
          return false;
        });
      });
    }
    const hasSame = handleSameColourData(obj?.colourWayRefsData);
    const hasEmptyCompositionIndex = obj?.composition?.findIndex((ele) => (ele?.composition && !ele?.percentage) || (ele?.percentage && !ele?.composition));
    if (!obj?.bomSubType || !obj?.referenceCode || hasEmptyIndex !== -1 || hasEmptyCompositionIndex !== -1 || hasSame) {
      if (hasSame) {
        const errIndex = obj?._id ? bomIndex : 0;
        bomCards[errIndex] = { ...bomCards[bomIndex], sameColours: true };
        setBomState((prevState) => ({ ...prevState, cardList: bomCards }));
      } else {
        const errIndex = obj?._id ? bomIndex : 0;
        bomCards[errIndex] = { ...bomCards[bomIndex], error: true };
        setBomState((prevState) => ({ ...prevState, cardList: bomCards }));
      }
    } else {
      handleColourway(colourArray, dataColour);

      const supportingDocIds = obj?.docIds?.map((e) => e?._id) || [];
      if (obj?._id) {
        forCroppedImage(obj);
      }
      const saveCard = withAssetImageKeys(obj);

      const params = {
        library: {
          ...saveCard,
          dynamicColumn: obj?.dynamicColumn?.map((ele) => ({
            colId: ele?.colId?._id || ele?.colId,
            valueId: ele?.valueId?._id || ele?.valueId,
            valueText: ele?.valueText,
          })),
          BOMSupplier: obj?.BOMSupplier?.map((supplier) => supplier?._id || supplier?.value),
          bomSubType: obj?.bomSubType?._id,
          bomType: obj?.bomType?._id,
          newPlacement: obj?.newPlacement?.map((ele) => ele?.value || ele?._id) || [],
          sizeUnitOfMeasure: obj?.sizeUnitOfMeasure?._id,
          weightUnitOfMeasure: obj?.weightUnitOfMeasure?._id,
          quantityUnitOfMeasure: obj?.quantityUnitOfMeasure?._id,
          finished: obj?.finished?._id,
          composition: handleComposition(obj?.composition),
          docIds: uniqDocIds(supportingDocIds)
            ?.filter((e) => e)
            ?.map((e) => e),
          colourWayRefsData: dataColour,
          createdBy: loggedInUserId || '',
          brand: obj?.brand || null,
          category1: obj?.category1 || null,
          category2: obj?.category2 || null,
          category3: obj?.category3 || null,
          category4: obj?.category4 || null,
          colorAutoApprove: obj?.colorAutoApprove || null,
          devOrderId,
        },
      };

      if (!obj?._id) {
        params.library.createdBy = loggedInUserId;
        params.library.bomType = bomState?.bomTypeId;
      }
      setBomState((prevState) => ({ ...prevState, loading: true }));
      const res = obj?._id
        ? await dispatch(action.updateCards(bomState?.bomTypeId, devOrderId, obj?._id, params, t))
        : await dispatch(action.createCards(bomState?.bomTypeId, devOrderId, params, t));
      if (res) {
        const optns = {
          pageIndex: 1,
          pageSize,
        };
        await dispatch(action.fetchCards(bomState?.bomTypeId, devOrderId, optns));
        await dispatch(supportingAction.unmountSupportingDocuments());
        await dispatch(libraryAction.clearColourDocs());
        setBomState((prevState) => ({
          ...prevState,
          loading: false,
          selectedIds: [],
          editBomDataId: [],
          isMultiEditing: false,
          imageUpload: false,
          uploadImageData: null,
          sendDocument: false,
          imageName: '',
          imageType: '',
          imageSize: '',
          originalImage: '',
          imgState: { mode: false, data: '' },
        }));
      }
    }
  };

  const handleMultiSave = async () => {
    const multiSaveCards = bomState?.cardList?.filter((ele) => bomState?.editBomDataId?.includes(ele?._id));
    const paramDetails = [];
    const hasSame = [];
    const errorIndexes = [];
    const hasEmptyIndex = [];
    const hasEmptyCompositionIndex = [];
    multiSaveCards?.forEach((multiSave, index) => {
      const colourArray = multiSave?.colourWayRefsData;
      if (handleSameColourData(multiSave?.colourWayRefsData)) {
        hasSame.push(handleSameColourData(multiSave?.colourWayRefsData));
        errorIndexes.push(index);
      }
      hasEmptyCompositionIndex.push(
        multiSave?.composition?.findIndex((ele) => (ele?.composition && !ele?.percentage) || (ele?.percentage && !ele?.composition)) !== -1 && index,
      );
      let hasEmptyIndexForCard = -1;
      const hasCombinationSuppliers = bomDetailsBasedOnId?.combinationSuppliers?.length > 0;
      if (hasCombinationSuppliers) {
        multiSave?.colourWayRefsData?.some((colourWay, colourWayIdx) => {
          return colourWay?.colourRefsDataAsPerSuppliers?.some((refData) => {
            if (!refData?.tier1Supplier?._id || !(refData?.componentSupplier?._id || refData?.componentSupplier?.value)) {
              hasEmptyIndexForCard = colourWayIdx;
              return true;
            }
            return false;
          });
        });
      }
      hasEmptyIndex.push(hasEmptyIndexForCard !== -1 && index);
      const dataColour = [];
      if (hasEmptyCompositionIndex.includes(index) || !multiSave?.bomSubType || !multiSave?.referenceCode || hasEmptyIndex.includes(index)) {
        const updatedCards = multiSaveCards?.map((ele, i) => {
          if (errorIndexes?.includes(i)) {
            return {
              ...ele,
              error: true,
            };
          }
          return ele;
        });
        setBomState((prev) => ({ ...prev, cardList: updatedCards }));
      } else {
        handleColourway(colourArray, dataColour);
        const supportingDocIds = multiSave?.docIds?.map((e) => e?._id) || [];
        const newmultiSave = withAssetImageKeys(forCroppedImageMultiple(multiSave));
        const params = {
          ...newmultiSave,
          BOMSupplier: multiSave?.BOMSupplier?.map((supplier) => supplier?._id || supplier?.value),
          bomSubType: multiSave?.bomSubType?._id,
          bomType: multiSave?.bomType?._id,
          newPlacement: multiSave?.newPlacement?.map((ele) => ele?.value || ele?._id) || [],
          sizeUnitOfMeasure: multiSave?.sizeUnitOfMeasure?._id,
          weightUnitOfMeasure: multiSave?.weightUnitOfMeasure?._id,
          quantityUnitOfMeasure: multiSave?.quantityUnitOfMeasure?._id,
          finished: multiSave?.finished?._id,
          // combinationSuppliers: combinationSupps,
          composition: handleComposition(multiSave?.composition),
          docIds: uniqDocIds(supportingDocIds)
            ?.filter((e) => e)
            ?.map((e) => e),
          colourWayRefsData: dataColour,
          createdBy: loggedInUserId || '',
          brand: multiSave?.brand || null,
          category1: multiSave?.category1 || null,
          category2: multiSave?.category2 || null,
          category3: multiSave?.category3 || null,
          category4: multiSave?.category4 || null,
          colorAutoApprove: multiSave?.colorAutoApprove || null,
          devOrderId,
          bomCustomAttributes: multiSave?.dynamicColumn?.map((attr) => attr?._id || attr?.value) || [],
        };
        if (!multiSave?._id) {
          params.createdBy = loggedInUserId;
          params.bomType = bomState?.bomTypeId;
        }
        paramDetails.push(params);
      }
    });
    if (hasSame?.includes(true)) {
      const updatedCards = multiSaveCards?.map((ele, i) => {
        if (errorIndexes?.includes(i)) {
          return {
            ...ele,
            sameColours: true,
          };
        }
        return ele;
      });
      setBomState((prev) => ({ ...prev, cardList: updatedCards }));
    } else {
      setBomState((prev) => ({ ...prev, loading: true }));
      const res = dispatch(action.updateMultiBoms(bomState?.bomTypeId, devOrderId, { bomData: uniqBy(paramDetails, '_id') }, t));
      if (res) {
        const optns = {
          pageIndex: 1,
          pageSize,
        };
        dispatch(action.fetchCards(bomState?.bomTypeId, devOrderId, optns));
        dispatch(supportingAction.unmountSupportingDocuments());
        dispatch(libraryAction.clearColourDocs());
        setBomState((prevState) => ({
          ...prevState,
          loading: false,
          selectedIds: [],
          editBomDataId: [],
          isMultiEditing: false,
          imageUpload: false,
          uploadImageData: null,
          sendDocument: false,
          imageName: '',
          imageType: '',
          imageSize: '',
          originalImage: '',
          imgState: { mode: false, data: '' },
          allSelected: false,
        }));
      }
    }
  };

  const handleColourAdd = (id) => {
    const copyCardList = cloneDeep(bomState?.cardList);
    const foundDataIndex = copyCardList?.findIndex((ele) => ele?._id === id);
    copyCardList[foundDataIndex].colourWayRefsData.push({
      colourRefsDataAsPerSuppliers: [
        {
          colourRef: null,
          colourRefNumber: '',
          supplier: null,
          comment: '',
          commentDate: null,
          approveBy: null,
          tier1Supplier: null,
          componentSupplier: null,
          colourRefName: null,
        },
      ],
      colourWayId: null,
    });
    if (foundDataIndex !== -1) {
      setBomState((prevState) => ({ ...prevState, cardList: copyCardList }));
    }
  };

  const handleColourDelete = (id, index) => {
    const copyCardList = cloneDeep(bomState?.cardList);
    const foundData = copyCardList?.find((ele) => ele?._id === id);
    const foundDataIndex = copyCardList?.findIndex((ele) => ele?._id === id);
    foundData?.colourWayRefsData?.splice(index, 1);
    if (foundDataIndex !== -1) {
      copyCardList[foundDataIndex] = foundData;
      setBomState((prevState) => ({ ...prevState, cardList: copyCardList }));
    }
  };

  const addBomToTable = () => {
    const originalCardList = bomState?.cardList;
    originalCardList.unshift({
      colourWayRefsData: [
        {
          colourWayId: null,
          colourRefsDataAsPerSuppliers: [
            {
              colourRef: null,
              colourRefNumber: '',
              supplier: null,
              comment: '',
              commentDate: null,
              approveBy: null,
              tier1Supplier: null,
              componentSupplier: null,
              colourRefName: null,
            },
          ],
        },
      ],
      image: '',
      bomSubType: null,
      referenceCode: '',
      bomDescription: '',
      BOMSupplier: [],
      newPlacement: null,
      size: '',
      sizeUnitOfMeasure: null,
      weight: '',
      weightUnitOfMeasure: null,
      quantity: '',
      quantityUnitOfMeasure: null,
      finished: null,
      composition: [{ percentage: null, composition: null }],
      additionalInfo: '',
      qualityAutoApprove: false,
      colorAutoApprove: false,
      bomApprovals: [],
      error: false,
      highlightedKeys: [],
    });
    setBomState((prevState) => ({ ...prevState, cardList: originalCardList, editBomDataId: [], selectedIds: [], isMultiEditing: false }));
  };

  const handleImageModalOpen = (data) => {
    setBomState((prevState) => ({ ...prevState, imageUpload: true, uploadImageData: data }));
  };

  const toggleUpload = () => {
    setBomState((prevState) => ({ ...prevState, imageUpload: false, uploadImageData: null }));
  };
  const formatFileSize = (bytes, decimalPoint) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1000;
    const dm = decimalPoint || 2;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`;
  };

  const showRemovepopup = (id) => {
    setBomState((old) => ({ ...old, showImageRemove: true, removeId: id }));
  };

  const toggleRemoveShow = () => {
    setBomState((old) => ({ ...old, showImageRemove: false, removeId: -1 }));
  };
  const removeImg = () => {
    const cards = cloneDeep(bomState?.cardList);
    const index = cards?.findIndex((ele) => ele?._id === bomState?.removeId);
    if (index !== -1) {
      cards[index].image = noImage;
      cards[index].removeImage = true;
      setBomState((prevState) => ({ ...prevState, cardList: cards }));
      toggleRemoveShow();
    }
  };

  const openPopup = (id) => {
    setBomState((prevState) => ({ ...prevState, openPopOverId: id }));
  };

  const contextValue = {
    bomState,
    setBomState,
    handleSelectAll,
    onSelectCheckBox,
    handleMergedPreview,
    OpenTestReq,
    toggleTestReq,
    toggleFP,
    openBomUsage,
    toggleBomUsage,
    handleImportLib,
    toggleAddToLibrary,
    handleEdit,
    handleCancel,
    handleExpandAllView,
    expandLineItem,
    handleChange,
    handleSave,
    handleMultiSave,
    handleColourAdd,
    handleColourDelete,
    addBomToTable,
    handleImageModalOpen,
    toggleUpload,
    formatFileSize,
    removeImg,
    showRemovepopup,
    toggleRemoveShow,
    openPopup,
    bomDetailsBasedOnId,
  };
  return (
    <StateContext.Provider
      value={{
        ...contextValue,
      }}
    >
      {children}
    </StateContext.Provider>
  );
}

export function useProductBomState() {
  const { t } = useTranslation('common');

  const context = useContext(StateContext);
  if (!context) {
    throw new Error(t('TAKING_DATES.USE_STATE_MESSAGE'));
  }
  return context;
}

/**
 * Swap a row's displayed Asset Library image (a pre-signed URL) for the asset's S3 key in the save
 * payload, so the backend references the existing asset instead of storing a short-lived URL.
 * Rows without an asset pick are returned unchanged apart from dropping the UI-only key field.
 */
function withAssetImageKeys(card) {
  const { assetImageKey, ...rest } = card || {};
  if (!rest?.isUploadAsset || !assetImageKey) return rest;
  return { ...rest, image: assetImageKey, fullImage: assetImageKey };
}
